if (!customElements.get('product-modal')) {
  function extractNumericId(val) {
    if (!val) return '';
    const str = String(val).trim();
    const match = str.match(/\d+$/);
    return match ? match[0] : str;
  }

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        // Handle navigation and close clicks inside modal
        this.addEventListener('click', (event) => {
          const prevBtn = event.target.closest('.product-media-modal__nav--prev');
          const nextBtn = event.target.closest('.product-media-modal__nav--next');
          const closeBtn = event.target.closest('.product-media-modal__toggle, [id^="ModalClose-"]');

          if (prevBtn) {
            event.preventDefault();
            event.stopPropagation();
            this.showPreviousMedia();
            return;
          }

          if (nextBtn) {
            event.preventDefault();
            event.stopPropagation();
            this.showNextMedia();
            return;
          }

          if (closeBtn) {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
            return;
          }

          // If clicked outside the content box (backdrop), close modal
          const content = this.querySelector('.product-media-modal__content');
          if (content && !content.contains(event.target)) {
            this.hide();
          }
        });

        // Prevent pointerup backdrop hide from triggering when clicking inside dialog/controls
        this.addEventListener('pointerup', (event) => {
          if (event.target.closest('.product-media-modal__dialog, .product-media-modal__nav, .product-media-modal__toggle')) {
            event.stopPropagation();
          }
        });

        // Keyboard arrow navigation
        this.addEventListener('keydown', (event) => {
          if (event.code === 'ArrowLeft') {
            event.preventDefault();
            this.showPreviousMedia();
          }
          if (event.code === 'ArrowRight') {
            event.preventDefault();
            this.showNextMedia();
          }
        });

        // Mouse wheel & trackpad scroll navigation
        let wheelLocked = false;
        this.addEventListener(
          'wheel',
          (event) => {
            if (wheelLocked) return;
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (Math.abs(delta) > 10) {
              event.preventDefault();
              wheelLocked = true;
              if (delta > 0) {
                this.showNextMedia();
              } else {
                this.showPreviousMedia();
              }
              setTimeout(() => {
                wheelLocked = false;
              }, 250);
            }
          },
          { passive: false }
        );

        // Touch swipe navigation
        let touchStartX = 0;
        let touchStartY = 0;
        this.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length === 1) {
              touchStartX = event.touches[0].clientX;
              touchStartY = event.touches[0].clientY;
            }
          },
          { passive: true }
        );

        this.addEventListener(
          'touchend',
          (event) => {
            if (event.changedTouches.length === 1) {
              const touchEndX = event.changedTouches[0].clientX;
              const touchEndY = event.changedTouches[0].clientY;
              const diffX = touchStartX - touchEndX;
              const diffY = touchStartY - touchEndY;

              if (Math.abs(diffX) > 35 && Math.abs(diffX) > Math.abs(diffY)) {
                if (diffX > 0) {
                  this.showNextMedia();
                } else {
                  this.showPreviousMedia();
                }
              }
            }
          },
          { passive: true }
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);

        let targetId = null;
        if (opener) {
          targetId = opener.getAttribute('data-media-id') || opener.dataset?.mediaId;
          if (!targetId) {
            const elWithId = opener.closest('[data-media-id]');
            if (elWithId) targetId = elWithId.getAttribute('data-media-id');
          }
        }

        if (!targetId) {
          const activeSlide = document.querySelector(
            'media-gallery .product__media-item.is-active, media-gallery li.is-active, media-gallery [data-media-id].is-active'
          );
          if (activeSlide) {
            targetId = activeSlide.getAttribute('data-media-id');
          }
        }

        this.showActiveMedia(targetId);
      }

      getMediaElements() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > *'));
      }

      getActiveIndex() {
        const elements = this.getMediaElements();
        const activeIdx = elements.findIndex((el) => el.classList.contains('active'));
        return activeIdx >= 0 ? activeIdx : 0;
      }

      showNextMedia() {
        const elements = this.getMediaElements();
        if (elements.length < 2) return;
        const currentIdx = this.getActiveIndex();
        const nextIdx = (currentIdx + 1) % elements.length;
        this.setActiveByIndex(nextIdx);
      }

      showPreviousMedia() {
        const elements = this.getMediaElements();
        if (elements.length < 2) return;
        const currentIdx = this.getActiveIndex();
        const prevIdx = (currentIdx - 1 + elements.length) % elements.length;
        this.setActiveByIndex(prevIdx);
      }

      setActiveByIndex(index) {
        const elements = this.getMediaElements();
        if (!elements.length) return;

        const safeIndex = Math.max(0, Math.min(index, elements.length - 1));

        elements.forEach((el, idx) => {
          if (idx === safeIndex) {
            el.classList.add('active');
            const displayVal = el.tagName === 'IMG' ? 'block' : 'flex';
            el.style.setProperty('display', displayVal, 'important');
            if (el.tagName === 'IMG' && el.loading === 'lazy') {
              el.loading = 'eager';
            }
          } else {
            el.classList.remove('active');
            el.style.setProperty('display', 'none', 'important');
          }
        });

        const activeEl = elements[safeIndex];

        // Update modal counter only
        const counter = this.querySelector('.product-media-modal__counter');
        if (counter) {
          counter.textContent = `${safeIndex + 1} / ${elements.length}`;
        }

        if (
          activeEl &&
          activeEl.nodeName === 'DEFERRED-MEDIA' &&
          typeof activeEl.loadContent === 'function'
        ) {
          activeEl.loadContent();
        }
      }

      showActiveMedia(mediaId) {
        const elements = this.getMediaElements();
        if (!elements.length) return;

        const rawTargetId = extractNumericId(mediaId);
        let matchedIndex = -1;

        if (rawTargetId) {
          matchedIndex = elements.findIndex(
            (el) => extractNumericId(el.getAttribute('data-media-id') || el.dataset?.mediaId) === rawTargetId
          );
        }

        if (matchedIndex === -1) {
          const gallerySlides = Array.from(
            document.querySelectorAll('media-gallery .product__media-item, media-gallery [id^="Slide-"]')
          );
          const activeGalleryIdx = gallerySlides.findIndex((slide) => slide.classList.contains('is-active'));
          matchedIndex = activeGalleryIdx >= 0 && activeGalleryIdx < elements.length ? activeGalleryIdx : 0;
        }

        this.setActiveByIndex(matchedIndex);
      }
    }
  );
}
